import { Option } from "rc-select";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { CheckedBoxBlueIcon, UncheckedBoxLightIcon } from "../../../assets";
import { SelectData } from "../../../models";

interface useAutocompleteMultiSelectProps {
  values?: string[] | null;
  //   initialKey?: string | null;
  data: SelectData;
  order?: string[];
  onChange: (values: string[]) => void;
  onTypeSearch?: (value?: string) => void;
}

interface useAutocompleteMultiSelectReturnData {
  selectedValues: string[] | null | undefined;
  searchValue: string | undefined;
  activeValue: string | null;
  onChangeInternal: (vals: string[]) => void;
  onTagMouseDown: (value: string, event: React.MouseEvent) => void;
  rootRef: React.RefObject<HTMLDivElement>;
  options: JSX.Element[];
  handleSearch: (newVal: string) => void;
}

const TYPE_SEARCH_WAITING_TIME = 100;

const buildOptions = (items: SelectData, selected: string[] | null | undefined): JSX.Element[] =>
  items.map((kvp) => (
    <Option key={kvp.key} value={kvp.key}>
      {selected?.includes(kvp.key as string) ? <CheckedBoxBlueIcon /> : <UncheckedBoxLightIcon />}
      {kvp.value}
    </Option>
  ));

export const useAutocompleteMultiSelect = ({
  values,
  data,
  order,
  onChange,
  onTypeSearch,
}: useAutocompleteMultiSelectProps): useAutocompleteMultiSelectReturnData => {
  const [selectedValues, setSelectedValues] = useState(values);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const [options, setOptions] = useState<JSX.Element[]>(buildOptions(data, values));
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedValues(values);
  }, [values]);

  const orderedData = useMemo<SelectData>(() => {
    if (order) {
      const updatedData: SelectData = [];

      order?.forEach((ord) => {
        const option = data.find((d) => d.key === ord);

        if (option) {
          updatedData.push(option);
        }
      });
      return updatedData;
    }

    return data;
  }, [order, data]);

  const onChangeInternal = (vals: string[]): void => {
    setSelectedValues(vals);
    onChange(vals);
    // Clear any typed search text and restore the full option list on every selection
    setSearchValue(undefined);
    setActiveValue(null);
    setOptions(buildOptions(orderedData, vals));
  };

  // Click a rendered item to "select" it; stop propagation so the dropdown doesn't toggle.
  const onTagMouseDown = (value: string, event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setActiveValue((current) => (current === value ? null : value));
  };

  // While an item is selected, Backspace/Delete de-selects it, and a mousedown or focus move
  // to anything outside this component clears the active tag.
  useEffect(() => {
    if (activeValue === null) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        onChangeInternal((selectedValues ?? []).filter((v) => v !== activeValue));
      }
    };

    // Use the capture phase so a sibling AutocompleteMultiSelect calling stopPropagation on
    // its own tag mousedown can't prevent this instance from clearing its active tag. The
    // contains() check scopes clearing to interactions outside this component, so only one
    // instance ever owns an active tag (and therefore an active keydown listener) at a time.
    const isOutside = (target: EventTarget | null): boolean =>
      !!rootRef.current && !rootRef.current.contains(target as Node);

    const handleOutsideMouseDown = (event: MouseEvent): void => {
      if (isOutside(event.target)) {
        setActiveValue(null);
      }
    };

    // Tabbing or programmatic focus into another field never fires a mousedown, so also clear
    // on focus leaving the component. Otherwise the document keydown listener would stay live
    // and hijack Backspace/Delete typed into that other field.
    const handleFocusIn = (event: FocusEvent): void => {
      if (isOutside(event.target)) {
        setActiveValue(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleOutsideMouseDown, true);
    document.addEventListener("focusin", handleFocusIn, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleOutsideMouseDown, true);
      document.removeEventListener("focusin", handleFocusIn, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue, selectedValues]);

  useEffect(() => {
    setOptions(buildOptions(orderedData, selectedValues));
  }, [orderedData]);

  const handleSearch = (newVal: string): void => {
    const filteredData = orderedData.filter((kvp) =>
      kvp.value?.toString().toLowerCase().includes(newVal.toLowerCase())
    );
    setOptions(buildOptions(filteredData, selectedValues));
    setSearchValue(newVal);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (onTypeSearch) {
        onTypeSearch(searchValue === "" ? undefined : searchValue);
      }
    }, TYPE_SEARCH_WAITING_TIME);

    return () => clearTimeout(debounce);
  }, [searchValue]);

  return {
    searchValue,
    selectedValues,
    activeValue,
    onChangeInternal,
    onTagMouseDown,
    rootRef,
    options,
    handleSearch,
  };
};
